import React, { useMemo, useState, useEffect, useRef, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "react-hot-toast";
import {
  ArrowLeft,
  BadgeCheck,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Clock,
  MessageSquare,
  ShieldCheck,
  User,
  UserCircle2,
  Vote,
  Paperclip,
  Image as ImageIcon,
  File,
  Link as LinkIcon,
  X,
  ExternalLink,
  DollarSign,
  Settings,
  CheckCircle,
  Wallet,
} from "lucide-react";
import api, { API_ENDPOINTS } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import { useSocket } from "../contexts/SocketContext";
import { useWeb3 } from "../contexts/Web3Context";
import { voterWithdrawal, resolveDisputeWithAuth } from "../config/DAOIntegration";
import { setDaoDisputeOnChainFromServer } from "../config/daoDisputePayout";
import { assertDaoMemberWalletForOnChain } from "../config/daoMemberWallet";
import { getLockedStakingBalance } from "../config/StakingIntegration";

const voteOptionLabels = {
  approve: "Approve",
  reject: "Reject",
  abstain: "Abstain",
  client_refund: "Client Refund",
  talent_refund: "Talent Refund",
  split_funds: "Split Funds",
};

function isInsufficientNativeGasError(error) {
  const raw = [error?.shortMessage, error?.message, error?.reason, error?.data?.message]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const code = error?.code;
  if (code === "INSUFFICIENT_FUNDS") return true;
  if (raw.includes("insufficient funds")) return true;
  if (raw.includes("gas required exceeds")) return true;
  if (raw.includes("intrinsic transaction cost")) return true;
  return false;
}

const GovernanceProposalDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { socket } = useSocket();
  const { isConnected, signer, provider, walletAddress, connectWallet } = useWeb3();
  const [voteReason, setVoteReason] = useState("");

  // Helper function to ensure wallet is connected
  const ensureWalletConnection = async () => {
    if (isConnected && walletAddress) {
      return true;
    }
    const result = await connectWallet();
    if (result && result.address) {
      return true;
    }
    toast.error('Please connect your wallet to continue.');
    return false;
  };

  // Helper function to resolve signer (ensures we have a valid signer, not just a provider)
  const resolveSigner = async () => {
    if (signer) return signer;
    if (provider) {
      try {
        return await provider.getSigner();
      } catch (error) {
        console.error('Unable to resolve signer from provider', error);
      }
    }
    return null;
  };

  /** Submit resolveDisputeWithAuth from the connected DAO member wallet (server-signed authorization). */
  const executeDisputeResolveOnChain = async (resolveDisputeData) => {
    await assertDaoMemberWalletForOnChain({
      user,
      isConnected,
      walletAddress,
      connectWallet,
    });

    const connected = await ensureWalletConnection();
    if (!connected) {
      throw new Error('Connect your wallet before resolving on-chain');
    }
    const signerInstance = await resolveSigner();
    if (!signerInstance) {
      throw new Error('Unable to access wallet signer. Reconnect your wallet and try again.');
    }

    if (!resolveDisputeData?.shouldCallContract || resolveDisputeData?.method !== 'resolveDisputeWithAuth') {
      throw new Error(
        'Server did not return resolveDisputeWithAuth data. Deploy the secured DAO contract and enable ALLOW_DAO_DISPUTE_PAYOUTS on the backend.'
      );
    }

    const contractResult = await resolveDisputeWithAuth(signerInstance, resolveDisputeData, {
      serverAuthorized: true,
    });
    return contractResult.txHash;
  };

  const [commentText, setCommentText] = useState("");
  const [messageText, setMessageText] = useState("");
  const [messageAttachments, setMessageAttachments] = useState([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [talentAmount, setTalentAmount] = useState("");
  const [clientAmount, setClientAmount] = useState("");
  const [votingEligibility, setVotingEligibility] = useState(null);
  const [lockedStakingBalance, setLockedStakingBalance] = useState('0');
  const [voterRewardAmount, setVoterRewardAmount] = useState('0');
  const [hasJustVoted, setHasJustVoted] = useState(false); // Track if user just voted to disable buttons immediately

  useQuery(
    ["governance", "dispute-payout-config"],
    async () => {
      const { data } = await api.get(API_ENDPOINTS.GOVERNANCE.DISPUTE_PAYOUT_CONFIG);
      setDaoDisputeOnChainFromServer(data?.enabled);
      return data;
    },
    { staleTime: 60_000 }
  );

  const {
    data: proposal,
    isLoading,
    isError,
    refetch: refetchProposal,
  } = useQuery(["governance", "proposal", id], async () => {
    const { data } = await api.get(API_ENDPOINTS.GOVERNANCE.DETAIL(id));
    return data;
  });

  // Fetch contract history for remaining amount calculation (for dispute proposals)
  const { data: contractHistory } = useQuery(
    ["governance", "contract-history", id],
    async () => {
      const { data } = await api.get(API_ENDPOINTS.GOVERNANCE.CONTRACT_HISTORY(id));
      return data;
    },
    {
      enabled: !!id && proposal?.proposalType === 'dispute',
      refetchOnWindowFocus: false,
    }
  );

  // Check voting eligibility
  useEffect(() => {
    const checkEligibility = async () => {
      if (!user) return;
      try {
        const { data } = await api.get(API_ENDPOINTS.GOVERNANCE.ELIGIBILITY);
        setVotingEligibility(data);
        
        // Also check locked staking balance if wallet is connected
        if (isConnected && provider && walletAddress) {
          try {
            const lockedBalance = await getLockedStakingBalance(provider, walletAddress);
            setLockedStakingBalance(lockedBalance);
          } catch (error) {
            console.error('Error fetching locked staking balance:', error);
          }
        }
        
        // Get voter reward amount from backend config (not contract)
        // It's included in the eligibility response
        if (data.voterRewardAmount !== undefined) {
          setVoterRewardAmount(data.voterRewardAmount?.toString() || '0');
        }
      } catch (error) {
        console.error('Error checking eligibility:', error);
      }
    };
    checkEligibility();
  }, [user, isConnected, provider, walletAddress]);

  const voteMutation = useMutation(
    async ({ vote, reason }) => {
      // Get reward amount from state (fetched from backend config)
      const rewardAmount = parseFloat(voterRewardAmount || '0');
      
      // Claim reward FIRST before recording vote
      if (rewardAmount > 0) {
        // Ensure wallet is connected
        const connected = await ensureWalletConnection();
        if (!connected) {
          throw new Error('Please connect your wallet to claim your voter reward before voting');
        }
        
        // Resolve signer (ensures we have a valid signer, not just a provider)
        const signerInstance = await resolveSigner();
        if (!signerInstance) {
          throw new Error('Unable to access wallet signer. Please try reconnecting your wallet.');
        }
        
        try {
          toast.loading('Claiming voter reward...');
          await voterWithdrawal(signerInstance, rewardAmount.toString());
          toast.dismiss();
          toast.success(`Successfully claimed ${rewardAmount} LOB tokens as voter reward!`);
        } catch (error) {
          toast.dismiss();
          console.error('Error claiming voter reward:', error);
          
          // Provide more detailed error message
          let errorMessage = 'Failed to claim reward. Please try again.';
          if (error?.message) {
            if (error.message.includes('user rejected') || error.message.includes('User denied')) {
              errorMessage = 'Transaction was rejected. Please try again.';
            } else if (error.message.includes('insufficient funds') || error.message.includes('gas')) {
              errorMessage = 'Insufficient funds for gas. Please ensure you have enough ETH for fees.';
            } else if (error.message.includes('contract runner does not support sending transactions')) {
              errorMessage = 'Wallet connection issue. Please reconnect your wallet and try again.';
            } else {
              errorMessage = `Failed to claim reward: ${error.message}`;
            }
          }
          throw new Error(errorMessage);
        }
      }
      
      // After successful reward claim, record the vote
      const response = await api.post(API_ENDPOINTS.GOVERNANCE.VOTE(id), {
        vote,
        reason: reason || undefined,
      });
      
      return response;
    },
    {
      onSuccess: async (response) => {
        toast.success("Vote recorded. +5 activity points added.");
        setVoteReason("");
        
        // Immediately disable vote buttons after successful vote
        setHasJustVoted(true);
        
        // Update the query cache immediately with the response data
        if (response?.data?.proposal) {
          queryClient.setQueryData(["governance", "proposal", id], response.data.proposal);
        }
        
        // Refetch immediately to ensure UI updates
        await refetchProposal();
        
        // Also invalidate to ensure fresh data everywhere
        queryClient.invalidateQueries(["governance", "proposal", id]);
        queryClient.invalidateQueries(["governance"]);
        queryClient.invalidateQueries(["governance", "leaderboard"]);
      },
      onError: async (error) => {
        // Handle different error types
        const errorMessage = error.message || error.response?.data?.error || "Unable to record your vote right now.";
        toast.error(errorMessage);
        
        // Reset hasJustVoted on error (vote didn't go through)
        setHasJustVoted(false);
        
        // If user already voted, update the proposal data to show their vote
        if (error.response?.data?.proposal) {
          queryClient.setQueryData(["governance", "proposal", id], error.response.data.proposal);
          await refetchProposal();
        }
      },
    }
  );

  const commentMutation = useMutation(
    () =>
      api.post(API_ENDPOINTS.GOVERNANCE.COMMENTS(id), {
        comment: String(commentText || "").trim(),
      }),
    {
      onSuccess: () => {
        toast.success("Comment shared with DAO members.");
        setCommentText("");
        queryClient.invalidateQueries(["governance", "proposal", id]);
      },
      onError: (error) => {
        const errs = error.response?.data?.errors;
        const first =
          Array.isArray(errs) && errs.length > 0
            ? errs.map((e) => e.msg || e.message).filter(Boolean).join(" ")
            : null;
        toast.error(
          first ||
            error.response?.data?.error ||
            "Unable to post comment at the moment."
        );
      },
    }
  );

  const messageMutation = useMutation(
    (data) =>
      api.post(API_ENDPOINTS.GOVERNANCE.DISPUTE_MESSAGES(id), data),
    {
      onSuccess: () => {
        toast.success("DAO message sent to dispute room.");
        setMessageText("");
        setMessageAttachments([]);
        queryClient.invalidateQueries(["governance", "proposal", id]);
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.error ||
            "Unable to send message to the dispute channel."
        );
      },
    }
  );

  const settlementMutation = useMutation(
    (data) => api.post(API_ENDPOINTS.GOVERNANCE.SETTLEMENT(id), data),
    {
      onSuccess: async (response) => {
        toast.success("Settlement amounts updated");
        if (response?.data?.proposal) {
          // Immediately update the cache with the new proposal data
          queryClient.setQueryData(["governance", "proposal", id], response.data.proposal);
        }
        // Refetch to ensure all data is synchronized
        await refetchProposal();
        // Invalidate to trigger any dependent queries
        queryClient.invalidateQueries(["governance", "proposal", id]);
        setShowSettlementModal(false);
      },
      onError: (error) => {
        console.error("Settlement mutation error:", error);
        toast.error(error.response?.data?.error || "Unable to update settlement amounts");
      },
    }
  );

  const settlementApproveMutation = useMutation(
    () => api.post(API_ENDPOINTS.GOVERNANCE.SETTLEMENT_APPROVE(id)),
    {
      onSuccess: async (response) => {
        toast.success(response.data?.message || "Settlement approved");
        if (response?.data?.proposal) {
          queryClient.setQueryData(["governance", "proposal", id], response.data.proposal);
        }
        await refetchProposal();
        queryClient.invalidateQueries(["governance", "proposal", id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || "Unable to approve settlement");
      },
    }
  );

  const settlementResolveMutation = useMutation(
    async () => {
      const callerWallet = await assertDaoMemberWalletForOnChain({
        user,
        isConnected,
        walletAddress,
        connectWallet,
      });
      const resolveResponse = await api.post(API_ENDPOINTS.GOVERNANCE.SETTLEMENT_RESOLVE(id), {
        callerWallet,
      });
      
      // Call the DAO contract first
      if (resolveResponse?.data?.resolveDisputeData?.shouldCallContract) {
        toast.loading('Resolving dispute on blockchain...');
        try {
          const txHash = await executeDisputeResolveOnChain(
            resolveResponse.data.resolveDisputeData
          );
          toast.dismiss();

          const confirmResponse = await api.post(API_ENDPOINTS.GOVERNANCE.SETTLEMENT_RESOLVE_CONFIRM(id), {
            txHash,
          });

          return confirmResponse;
        } catch (error) {
          toast.dismiss();
          console.error('Error calling resolveDispute contract:', error);
          throw new Error(error.message || 'Contract call failed. Please try again.');
        }
      }
      
      // Should not reach here for disputes, but handle gracefully
      return resolveResponse;
    },
    {
      onSuccess: (response) => {
        toast.success(response.data?.message || "Dispute resolved by mutual agreement");
        if (response?.data?.proposal) {
          queryClient.setQueryData(["governance", "proposal", id], response.data.proposal);
        }
        refetchProposal();
        queryClient.invalidateQueries(["governance", "proposal", id]);
        queryClient.invalidateQueries(["governance"]);
      },
      onError: (error) => {
        toast.dismiss();
        toast.error(error.response?.data?.error || error.message || "Unable to resolve dispute");
      },
    }
  );

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const newAttachments = files.map(file => ({
      file,
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));

    setMessageAttachments(prev => [...prev, ...newAttachments]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (attachmentId) => {
    setMessageAttachments(prev => {
      const attachment = prev.find(a => a.id === attachmentId);
      if (attachment?.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return prev.filter(a => a.id !== attachmentId);
    });
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() && messageAttachments.length === 0) {
      toast.error("Please enter a message or attach a file.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append('message', messageText || '');
      
      messageAttachments.forEach((attachment, index) => {
        formData.append(`attachments`, attachment.file);
      });

      await messageMutation.mutateAsync(formData);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const resolveMutation = useMutation(
    async () => {
      const callerWallet = await assertDaoMemberWalletForOnChain({
        user,
        isConnected,
        walletAddress,
        connectWallet,
      });
      const resolveResponse = await api.post(API_ENDPOINTS.GOVERNANCE.RESOLVE(id), {
        callerWallet,
      });
      
      // For dispute proposals, call the contract first
      if (resolveResponse?.data?.resolveDisputeData?.shouldCallContract) {
        toast.loading('Resolving dispute on blockchain...');
        try {
          const txHash = await executeDisputeResolveOnChain(
            resolveResponse.data.resolveDisputeData
          );
          toast.dismiss();

          const confirmResponse = await api.post(API_ENDPOINTS.GOVERNANCE.RESOLVE_CONFIRM(id), {
            txHash,
            resolutionType: resolveResponse.data.resolveDisputeData.resolutionType,
            finalDecision: resolveResponse.data.resolveDisputeData.finalDecision,
            clientAmountUSD: resolveResponse.data.resolveDisputeData.clientAmountUSD,
            talentAmountUSD: resolveResponse.data.resolveDisputeData.talentAmountUSD,
          });

          return confirmResponse;
        } catch (error) {
          toast.dismiss();
          console.error('Error calling resolveDispute contract:', error);
          throw new Error(error.message || 'Contract call failed. Please try again.');
        }
      }
      
      // For non-dispute proposals, just return the response
      return resolveResponse;
    },
    {
      onSuccess: (response) => {
        toast.success(response.data?.message || "Proposal marked as resolved.");
        queryClient.invalidateQueries(["governance", "proposal", id]);
        queryClient.invalidateQueries(["governance"]);
        refetchProposal();
      },
      onError: (error) => {
        toast.dismiss();
        toast.error(
          error.response?.data?.error || error.message || "Unable to resolve proposal now."
        );
      },
    }
  );

  const backToGovernance = () => navigate("/governance");

  const voteOptions = useMemo(
    () => proposal?.voteOptions || [],
    [proposal?.voteOptions]
  );

  const voters = useMemo(
    () => proposal?.votes || [],
    [proposal?.votes]
  );

  // Pagination for voting ledger
  const [votingPage, setVotingPage] = useState(1);
  const votesPerPage = 20;
  const totalVotingPages = Math.ceil(voters.length / votesPerPage);
  const paginatedVoters = useMemo(() => {
    const start = (votingPage - 1) * votesPerPage;
    const end = start + votesPerPage;
    return voters.slice(start, end);
  }, [voters, votingPage]);

  // Get the user's vote if they have voted - use useMemo to recalculate when proposal changes
  // Must be called before any early returns (React Hooks rule)
  const userVote = useMemo(() => {
    if (!user || !proposal?.votes || !Array.isArray(proposal.votes)) {
      return null;
    }
    
    const currentUserId = user._id || user.id;
    if (!currentUserId) {
      return null;
    }
    
    const currentUserIdStr = currentUserId.toString();
    
    // Find the user's vote - handle both populated and unpopulated user objects
    const foundVote = proposal.votes.find(v => {
      if (!v || !v.user) return false;
      
      // Handle populated user object (has _id property)
      if (v.user._id) {
        return v.user._id.toString() === currentUserIdStr;
      }
      // Handle unpopulated user (just ObjectId string)
      if (typeof v.user === 'string') {
        return v.user.toString() === currentUserIdStr;
      }
      // Handle user object with id property
      if (v.user.id) {
        return v.user.id.toString() === currentUserIdStr;
      }
      
      return false;
    });
    
    return foundVote || null;
  }, [user, proposal?.votes, proposal?.viewerContext?.hasVoted]);
  
  // Determine if user has voted - check multiple sources for reliability
  // 1. Backend viewerContext.hasVoted (primary, persists after refresh)
  // 2. userVote existence (also persists after refresh, more reliable)
  // 3. hasJustVoted (temporary local state, only during voting process)
  const hasVotedFromBackend = Boolean(proposal?.viewerContext?.hasVoted || userVote);
  const hasVoted = hasVotedFromBackend || hasJustVoted;

  const settlement = proposal?.disputeContext?.settlement;
  const clientId = proposal?.disputeContext?.client?._id || proposal?.disputeContext?.client;
  const talentId = proposal?.disputeContext?.talent?._id || proposal?.disputeContext?.talent;
  const userId = user?._id || user?.id;
  const isClient = !!(userId && clientId && userId.toString() === clientId.toString());
  const isTalent = !!(userId && talentId && userId.toString() === talentId.toString());
  // Any DAO member with voting rights (9+ points) can set settlement, even if they voted
  const canSetSettlement = !!(proposal?.viewerContext?.canVote && !isClient && !isTalent);
  const bothApproved = !!(settlement?.clientApproved && settlement?.talentApproved);
  const canResolveSettlement = !!(proposal?.viewerContext?.canComment && bothApproved && !settlement?.settledByAgreement && isConnected);

  // Update local state when proposal data changes
  useEffect(() => {
    if (!proposal) return;
    
    const currentSettlement = proposal?.disputeContext?.settlement;
    if (currentSettlement) {
      const talentAmt = currentSettlement.talentAmount !== undefined && currentSettlement.talentAmount !== null
        ? currentSettlement.talentAmount.toString()
        : "";
      const clientAmt = currentSettlement.clientAmount !== undefined && currentSettlement.clientAmount !== null
        ? currentSettlement.clientAmount.toString()
        : "";
      
      setTalentAmount(talentAmt);
      setClientAmount(clientAmt);
    } else {
      setTalentAmount("");
      setClientAmount("");
    }
  }, [
    proposal?._id,
    proposal?.disputeContext?.settlement?.talentAmount,
    proposal?.disputeContext?.settlement?.clientAmount,
    proposal?.disputeContext?.settlement?.talentApproved,
    proposal?.disputeContext?.settlement?.clientApproved
  ]);

  // Listen for real-time proposal updates via socket
  useEffect(() => {
    if (!socket || !id) return;

    const handleProposalUpdate = (data) => {
      if (data.proposalId === id && data.proposal) {
        // Update the query cache with the new proposal data
        queryClient.setQueryData(["governance", "proposal", id], data.proposal);
        // Refetch to ensure all derived data is updated
        refetchProposal();
      }
    };

    socket.on('proposal:updated', handleProposalUpdate);

    return () => {
      socket.off('proposal:updated', handleProposalUpdate);
    };
  }, [socket, id, queryClient, refetchProposal]);

  if (isLoading) {
    return (
      <div className="p-6">
        <button className="btn-outline mb-4" onClick={backToGovernance}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to governance
        </button>
        <div className="card p-12 flex items-center justify-center">
          <div className="animate-spin h-10 w-10 border-4 border-accent border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  if (isError || !proposal) {
    return (
      <div className="p-6 space-y-4">
        <button className="btn-outline" onClick={backToGovernance}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to governance
        </button>
        <div className="card p-12 text-center space-y-3">
          <p className="text-5xl">🤔</p>
          <h3 className="text-xl font-semibold text-heading">
            Proposal unavailable
          </h3>
          <p className="text-gray-500">
            We couldn't retrieve that proposal. It may have been removed or you
            might not have access.
          </p>
        </div>
      </div>
    );
  }

  const hasVotingClosed = proposal?.voting?.timeRemainingMs === 0;
  const canVoteBackend = proposal?.viewerContext?.canVote;
  
  // Enhanced eligibility check: 20+ activity points AND configured minimum LOB locked staking
  const hasMinActivityPoints = votingEligibility?.eligible || false;
  const minLockedStaking = votingEligibility?.requiredLockedStaking || 100;
  const hasMinLockedStaking = parseFloat(lockedStakingBalance || '0') >= minLockedStaking;
  const canVote = canVoteBackend && hasMinActivityPoints && hasMinLockedStaking && isConnected;
  
  const canComment = proposal?.viewerContext?.canComment;
  const canResolve = proposal?.viewerContext?.canResolve && isConnected;
  const minPointsRequired = 20; // Updated to 20
  
  // hasVoted is already defined above before early returns for useMemo

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <button className="btn-outline" onClick={backToGovernance}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Governance hub
        </button>

        {canResolve && (
          <button
            className="btn-primary flex items-center"
            onClick={() => resolveMutation.mutate()}
            disabled={resolveMutation.isLoading}
          >
            <BadgeCheck className="w-4 h-4 mr-2" />
            {resolveMutation.isLoading ? "Resolving..." : "Mark as resolved"}
          </button>
        )}
      </div>

      <header className="card p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200 text-xs font-semibold uppercase tracking-wide">
            {proposal.proposalType === "platform" ? "Platform upgrade" : "Dispute resolution"}
          </span>
          <StatusPill status={proposal.status} />
          <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-300">
            Requires {minPointsRequired}+ activity points to vote
          </span>
        </div>
        <div className="space-y-2">
          <h1 className="text-3xl font-heading font-bold text-heading">
            {proposal.title}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-3xl">
            {proposal.summary || proposal.description}
          </p>
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <UserCircle2 className="w-4 h-4" />
            Proposed by {proposal.proposer?.username || "Unknown member"}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            Created {formatDistanceToNow(new Date(proposal.createdAt))} ago
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            Voting deadline:{" "}
            {proposal.voting.endsAt
              ? format(new Date(proposal.voting.endsAt), "PPpp")
              : "N/A"}
          </span>
        </div>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-6 space-y-4 lg:col-span-2">
          <div className="flex items-center gap-2 text-heading font-semibold">
            <Vote className="w-5 h-5 text-accent" />
            Voting panel
          </div>
          <p className="text-sm text-gray-500">
            Members with at least {minPointsRequired} activity points can vote.
            Voting period lasts {votingEligibility?.votingDurationDays || proposal?.voting?.durationDays || 5} days. DAO members earn +{votingEligibility?.activityPointsForVoting || 5} points per vote.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`rounded-lg border-2 p-4 space-y-2 ${
              proposal.voting.timeRemainingMs > 0 && !bothApproved
                ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20' 
                : bothApproved
                ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20'
                : 'border-gray-200 dark:border-gray-700'
            }`}>
              <div className="flex items-center gap-2">
                <Clock className={`w-4 h-4 ${
                  bothApproved 
                    ? 'text-green-600 dark:text-green-400' 
                    : proposal.voting.timeRemainingMs > 0 
                    ? 'text-amber-600 dark:text-amber-400' 
                    : 'text-gray-400'
                }`} />
                <h4 className="text-xs uppercase text-gray-500 font-semibold">Voting Countdown</h4>
              </div>
              <p className={`text-3xl font-bold ${
                bothApproved
                  ? 'text-green-600 dark:text-green-400'
                  : proposal.voting.timeRemainingMs > 0 
                  ? 'text-amber-700 dark:text-amber-300' 
                  : 'text-gray-700 dark:text-gray-300'
              }`}>
                {bothApproved 
                  ? "Stopped by Agreement"
                  : proposal.voting.timeRemainingLabel}
              </p>
              <p className={`text-xs ${
                bothApproved 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-gray-500'
              }`}>
                {bothApproved
                  ? "Both parties approved settlement. Voting countdown stopped."
                  : proposal.voting.timeRemainingMs > 0 
                  ? `Ends at ${proposal.voting.endsAt ? format(new Date(proposal.voting.endsAt), "PPpp") : "N/A"}`
                  : "Voting period has ended"}
              </p>
              {proposal.voting.timeRemainingMs > 0 && !bothApproved && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                    <div 
                      className="bg-amber-500 h-1.5 rounded-full transition-all"
                      style={{
                        width: `${Math.min((proposal.voting.timeRemainingMs / (5 * 24 * 60 * 60 * 1000)) * 100, 100)}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-2">
              <h4 className="text-xs uppercase text-gray-500">Participation</h4>
              <p className="text-2xl font-semibold text-heading">
                {proposal.voteTallies.total} voters
              </p>
              <p className="text-xs text-gray-500">
                {proposal.analytics.participationRate}% of eligible DAO
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium text-heading">Vote options</p>
              {!canVote && (
                <div className="flex flex-col gap-1 text-xs text-amber-600">
                  {!hasMinActivityPoints && (
                    <span>You need {minPointsRequired}+ activity points to vote. Current: {votingEligibility?.activityPoints || 0}</span>
                  )}
                  {hasMinActivityPoints && !hasMinLockedStaking && (
                    <span>You need {minLockedStaking}+ LOB locked staking to vote. Current: {parseFloat(lockedStakingBalance || '0').toFixed(2)} LOB</span>
                  )}
                  {hasMinActivityPoints && hasMinLockedStaking && !isConnected && (
                    <span>Please connect your wallet to vote</span>
                  )}
                  {!votingEligibility && (
                    <span>Checking eligibility...</span>
                  )}
                </div>
              )}
              {hasVoted && (
                <span className="text-xs text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" /> Vote recorded
                </span>
              )}
              {hasVotingClosed && !proposal.viewerContext.hasVoted && (
                <span className="text-xs text-gray-500">
                  Voting has ended. You can no longer vote.
                </span>
              )}
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {voteOptions.map((option) => {
                  // Check if this option is the user's vote
                  const isUserVote = Boolean(
                    userVote && 
                    userVote.vote && 
                    option.id && 
                    userVote.vote === option.id
                  );
                  
                  // Determine if button should be disabled
                  const isDisabled = !canVote || hasVotingClosed || hasVoted || hasJustVoted || voteMutation.isLoading;
                  
                  return (
                    <button
                      key={option.id}
                      disabled={isDisabled}
                      onClick={(e) => {
                        if (isDisabled) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        voteMutation.mutate({ vote: option.id, reason: voteReason });
                      }}
                      className={`rounded-xl border px-4 py-3 text-left transition relative ${
                        isDisabled
                          ? isUserVote
                            ? "border-[#00a19e] bg-[#00a19e]/20 dark:bg-[#00a19e]/30 opacity-70 cursor-not-allowed pointer-events-none"
                            : "border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed pointer-events-none"
                          : isUserVote
                          ? "text-white shadow-lg cursor-pointer"
                          : "hover:border-accent hover:bg-accent/10 border-gray-200 dark:border-gray-700 cursor-pointer"
                      }`}
                      style={isUserVote && !isDisabled ? {
                        backgroundColor: '#00a19e',
                        borderColor: '#00a19e',
                        boxShadow: '0 0 0 2px rgba(0, 161, 158, 0.3)'
                      } : isDisabled ? {
                        cursor: 'not-allowed',
                        pointerEvents: 'none'
                      } : undefined}
                    >
                      {isUserVote && (
                        <div className="absolute top-2 right-2">
                          <CheckCircle2 className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <p className={`font-medium ${isUserVote ? "text-white dark:text-white" : "text-heading"}`}>
                        {option.label}
                        {isUserVote && (
                          <span className="ml-2 text-xs font-normal text-white/90 dark:text-white/90">
                            (Your vote)
                          </span>
                        )}
                      </p>
                      <p className={`text-xs ${isUserVote ? "text-white/90 dark:text-white/90" : "text-gray-500"}`}>
                        {option.votes} votes
                      </p>
                    </button>
                  );
                })}
              </div>
              
              {/* Display user's vote selection */}
              {userVote && (
                <div className="mt-4 p-4 rounded-xl border-2 border-[#00a19e] bg-[#00a19e]/10 dark:bg-[#06beb6]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-[#00a19e] dark:text-[#06beb6]" />
                    <h4 className="text-sm font-semibold text-heading">Your Vote</h4>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-base font-medium text-heading">
                        {voteOptionLabels[userVote.vote] || userVote.vote}
                      </p>
                      {userVote.reason && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {userVote.reason}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        Voted {userVote.votedAt ? formatDistanceToNow(new Date(userVote.votedAt), { addSuffix: true }) : 'recently'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* <div>
                <label className="text-xs uppercase text-gray-500">
                  Optional vote reason (visible to DAO members)
                </label>
                <textarea
                  className="input-field mt-1 h-20"
                  value={voteReason}
                  onChange={(event) => setVoteReason(event.target.value)}
                  placeholder="Share your rationale for this vote..."
                />
              </div> */}
            </div>
          </div>
        </div>

        <aside className="card p-6 space-y-4">
          <div className="flex items-center gap-2 text-heading font-semibold">
            <ClipboardList className="w-5 h-5 text-accent" />
            Voting ledger
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="max-h-72 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr className="text-left text-xs uppercase text-gray-500">
                    <th className="px-4 py-2">Member</th>
                    <th className="px-4 py-2">Vote</th>
                    <th className="px-4 py-2">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {voters.length === 0 && (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        No votes recorded yet.
                      </td>
                    </tr>
                  )}
                  {paginatedVoters.map((entry) => (
                    <tr
                      key={entry._id}
                      className="border-t border-gray-100 dark:border-gray-800"
                    >
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          {entry.user?.username || "Member"}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        {voteOptionLabels[entry.vote] || entry.vote}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {format(new Date(entry.votedAt), "PPpp")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalVotingPages > 1 && (
              <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Showing {((votingPage - 1) * votesPerPage) + 1} to {Math.min(votingPage * votesPerPage, voters.length)} of {voters.length} votes
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setVotingPage(prev => Math.max(1, prev - 1))}
                    disabled={votingPage === 1}
                    className="px-3 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-gray-500">
                    Page {votingPage} of {totalVotingPages}
                  </span>
                  <button
                    onClick={() => setVotingPage(prev => Math.min(totalVotingPages, prev + 1))}
                    disabled={votingPage === totalVotingPages}
                    className="px-3 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
          {proposal.voting.finalDecision && (
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/40 p-4 text-sm text-emerald-700 dark:text-emerald-200 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-1" />
              <div>
                <p className="font-semibold">
                  Outcome:{" "}
                  {voteOptionLabels[proposal.voting.finalDecision] ||
                    proposal.voting.finalDecision}
                </p>
                {proposal.resolution?.decidedAt && (
                  <p className="text-xs">
                    Decided {format(new Date(proposal.resolution.decidedAt), "PPpp")}
                  </p>
                )}
              </div>
            </div>
          )}
        </aside>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-6 space-y-4 lg:col-span-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-accent" />
            <h3 className="text-lg font-semibold text-heading">DAO commentary</h3>
          </div>
          {proposal.proposalType === "dispute" ? (
            <DisputeDiscussion
              proposal={proposal}
              messageText={messageText}
              onMessageChange={setMessageText}
              onSendMessage={handleSendMessage}
              messageAttachments={messageAttachments}
              onFileSelect={handleFileSelect}
              onRemoveAttachment={removeAttachment}
              fileInputRef={fileInputRef}
              commentText={commentText}
              onCommentChange={setCommentText}
              onAddComment={() => commentMutation.mutate()}
              canSendMessage={canComment}
              canComment={canComment}
              isSending={messageMutation.isLoading}
              isCommentSubmitting={commentMutation.isLoading}
              user={user}
              canSetSettlement={canSetSettlement}
              settlement={settlement}
              onOpenSettlementModal={() => setShowSettlementModal(true)}
              onApproveSettlement={() => settlementApproveMutation.mutate()}
              approveLoading={settlementApproveMutation.isLoading}
              canResolveSettlement={canResolveSettlement}
              onResolveSettlement={() => settlementResolveMutation.mutate()}
              resolveLoading={settlementResolveMutation.isLoading}
              bothApproved={bothApproved}
            />
          ) : (
            <PlatformComments
              proposal={proposal}
              commentText={commentText}
              onCommentChange={setCommentText}
              onAddComment={() => commentMutation.mutate()}
              canComment={canComment}
              isSubmitting={commentMutation.isLoading}
            />
          )}
        </div>

        <div className="card p-6 space-y-4">
          {proposal.proposalType === "platform" ? (
            <PlatformDetails proposal={proposal} />
          ) : (
            <DisputeDetails proposal={proposal} />
          )}
        </div>
      </section>

      {/* Settlement Modal */}
      {showSettlementModal && (() => {
        // Calculate remaining amount and dynamic percentage limit
        const remainingAmount = contractHistory?.remainingAmount || 0;
        const settlementPercentage = contractHistory?.settlementPercentage || 90;
        const maxSettlementAmount = remainingAmount * (settlementPercentage / 100);
        const currentTotal = (parseFloat(talentAmount) || 0) + (parseFloat(clientAmount) || 0);
        const exceedsLimit = currentTotal > maxSettlementAmount;
        const remainingForSettlement = maxSettlementAmount - currentTotal;

        // Auto-adjust handler for talent amount
        const handleTalentAmountChange = (value) => {
          if (value === '' || value === null || value === undefined) {
            setTalentAmount('');
            // When cleared, set client to max amount
            setClientAmount(maxSettlementAmount.toFixed(2));
            return;
          }
          
          const talentVal = parseFloat(value) || 0;
          
          // Cap at max settlement amount
          if (talentVal > maxSettlementAmount) {
            setTalentAmount(maxSettlementAmount.toFixed(2));
            setClientAmount('0');
          } else if (talentVal < 0) {
            setTalentAmount('0');
            setClientAmount(maxSettlementAmount.toFixed(2));
          } else {
            setTalentAmount(value);
            // Auto-adjust client amount to use remaining (up to 90% total)
            const remaining = maxSettlementAmount - talentVal;
            setClientAmount(remaining >= 0 ? remaining.toFixed(2) : '0');
          }
        };

        // Auto-adjust handler for client amount
        const handleClientAmountChange = (value) => {
          if (value === '' || value === null || value === undefined) {
            setClientAmount('');
            // When cleared, set talent to max amount
            setTalentAmount(maxSettlementAmount.toFixed(2));
            return;
          }
          
          const clientVal = parseFloat(value) || 0;
          
          // Cap at max settlement amount
          if (clientVal > maxSettlementAmount) {
            setClientAmount(maxSettlementAmount.toFixed(2));
            setTalentAmount('0');
          } else if (clientVal < 0) {
            setClientAmount('0');
            setTalentAmount(maxSettlementAmount.toFixed(2));
          } else {
            setClientAmount(value);
            // Auto-adjust talent amount to use remaining (up to 90% total)
            const remaining = maxSettlementAmount - clientVal;
            setTalentAmount(remaining >= 0 ? remaining.toFixed(2) : '0');
          }
        };

        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 max-w-md w-full p-6 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-heading">Set Settlement Amounts</h3>
                <button
                  onClick={() => setShowSettlementModal(false)}
                  className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Remaining Amount Info */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Remaining (USDC):</span>
                  <span className="text-lg font-semibold text-heading">{remainingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Max Settlement ({settlementPercentage}%):</span>
                  <span className="text-lg font-semibold text-accent">{maxSettlementAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC</span>
                </div>
                {currentTotal > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Total:</span>
                    <span className={`text-sm font-semibold ${exceedsLimit ? 'text-red-600 dark:text-red-400' : 'text-heading'}`}>
                      {currentTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC
                    </span>
                  </div>
                )}
                {exceedsLimit && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    Total exceeds {settlementPercentage}% limit. Please adjust amounts.
                  </p>
                )}
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                Split up to {settlementPercentage}% of the remaining escrow (USDC) between talent and client. As you enter one amount, the other will auto-adjust.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-heading mb-2">
                    Talent payout (USDC)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={maxSettlementAmount}
                    value={talentAmount}
                    onChange={(e) => handleTalentAmountChange(e.target.value)}
                    className={`input-field w-full ${exceedsLimit && parseFloat(talentAmount) > 0 ? 'border-red-300 dark:border-red-700' : ''}`}
                    placeholder="0.00"
                  />
                  {parseFloat(talentAmount) > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Client will receive: {(maxSettlementAmount - (parseFloat(talentAmount) || 0)).toFixed(2)} USDC
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-heading mb-2">
                    Client payout (USDC)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={maxSettlementAmount}
                    value={clientAmount}
                    onChange={(e) => handleClientAmountChange(e.target.value)}
                    className={`input-field w-full ${exceedsLimit && parseFloat(clientAmount) > 0 ? 'border-red-300 dark:border-red-700' : ''}`}
                    placeholder="0.00"
                  />
                  {parseFloat(clientAmount) > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Talent will receive: {(maxSettlementAmount - (parseFloat(clientAmount) || 0)).toFixed(2)} USDC
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowSettlementModal(false)}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const data = {};
                    if (talentAmount) data.talentAmount = parseFloat(talentAmount);
                    if (clientAmount) data.clientAmount = parseFloat(clientAmount);
                    settlementMutation.mutate(data);
                  }}
                  disabled={settlementMutation.isLoading || (!talentAmount && !clientAmount) || exceedsLimit}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {settlementMutation.isLoading ? "Saving..." : "Save Amounts"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const PlatformComments = ({
  proposal,
  commentText,
  onCommentChange,
  onAddComment,
  canComment,
  isSubmitting,
}) => (
  <div className="space-y-4">
    <p className="text-sm text-gray-500">
      Only DAO members with 9+ activity points can join this strategic
      discussion thread.
    </p>
    <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
      {proposal.comments?.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-6 text-center text-sm text-gray-500">
          No comments yet. Be the first to share a perspective.
        </div>
      )}
      {proposal.comments?.map((comment) => (
        <div
          key={comment._id}
          className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/40"
        >
          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
            <span className="font-medium text-heading">
              {comment.user?.username || "DAO member"}
            </span>
            <span>
              {formatDistanceToNow(new Date(comment.createdAt), {
                addSuffix: true,
              })}
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
            {comment.comment}
          </p>
        </div>
      ))}
    </div>
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
      <label className="text-xs uppercase text-gray-500">
        Add a DAO comment
      </label>
      <textarea
        className="input-field mt-2 h-28"
        placeholder={
          canComment
            ? "Share structured feedback, risks, or suggested amendments..."
            : "You need 9+ activity points to comment."
        }
        value={commentText}
        onChange={(event) => onCommentChange(event.target.value)}
        disabled={!canComment}
      />
      <div className="flex justify-end mt-3">
        <button
          className="btn-primary"
          disabled={!canComment || !commentText || isSubmitting}
          onClick={onAddComment}
        >
          {isSubmitting ? "Posting..." : "Share comment"}
        </button>
      </div>
    </div>
  </div>
);

const DisputeDiscussion = ({
  proposal,
  messageText,
  onMessageChange,
  onSendMessage,
  messageAttachments,
  onFileSelect,
  onRemoveAttachment,
  fileInputRef,
  commentText,
  onCommentChange,
  onAddComment,
  canSendMessage,
  canComment,
  isSending,
  isCommentSubmitting,
  user,
  canSetSettlement,
  settlement,
  onOpenSettlementModal,
  onApproveSettlement,
  approveLoading,
  canResolveSettlement,
  onResolveSettlement,
  resolveLoading,
  bothApproved,
}) => {
  const messagesEndRef = useRef(null);
  
  // Fetch contract history
  const { data: contractHistory, isLoading: isLoadingHistory } = useQuery(
    ["governance", "contract-history", proposal._id],
    async () => {
      const { data } = await api.get(API_ENDPOINTS.GOVERNANCE.CONTRACT_HISTORY(proposal._id));
      return data;
    },
    {
      enabled: !!proposal._id && proposal.proposalType === 'dispute',
      refetchOnWindowFocus: false,
    }
  );
  
  // Combine chat messages and collaboration thread
  const chatMessages = proposal.chatData?.messages || [];
  const collaborationMessages = proposal.disputeContext?.collaborationThread || [];
  
  // Determine if user is client or talent for chat messages
  const clientId = proposal.disputeContext?.client?._id || proposal.disputeContext?.client;
  const talentId = proposal.disputeContext?.talent?._id || proposal.disputeContext?.talent;
  
  const isClient = user && clientId && (
    user._id?.toString() === clientId.toString() || 
    user.id?.toString() === clientId.toString()
  );
  const isTalent = user && talentId && (
    user._id?.toString() === talentId.toString() || 
    user.id?.toString() === talentId.toString()
  );
  const isDaoMember = user && canSendMessage;

  // Merge and sort all messages chronologically (previous chat + DAO collaboration)
  const allMessages = [
    ...chatMessages.map(msg => {
      const msgSenderId = msg.senderId?._id || msg.senderId;
      const msgClientId = clientId?.toString();
      const msgTalentId = talentId?.toString();
      const senderIdStr = msgSenderId?.toString();
      
      return {
        ...msg,
        _id: msg._id,
        source: 'chat',
        role: senderIdStr === msgClientId ? 'client' :
              senderIdStr === msgTalentId ? 'talent' : 'dao',
        sender: msg.senderId?.username || 'Unknown',
        senderId: msgSenderId,
        content: msg.content || msg.message,
        createdAt: msg.timestamp || msg.createdAt
      };
    }),
    ...collaborationMessages.map(msg => {
      const msgSenderId = msg.sender?._id || msg.sender;
      const msgClientId = clientId?.toString();
      const msgTalentId = talentId?.toString();
      const senderIdStr = msgSenderId?.toString();
      
      // Determine role: if sender is client or talent, use that role; otherwise DAO
      let role = 'dao';
      if (senderIdStr === msgClientId) {
        role = 'client';
      } else if (senderIdStr === msgTalentId) {
        role = 'talent';
      }
      
      return {
        ...msg,
        source: 'dao',
        role: role,
        sender: msg.sender?.username || 'DAO Member',
        senderId: msgSenderId,
        content: msg.message,
        createdAt: msg.createdAt || msg.timestamp
      };
    })
  ].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages.length, collaborationMessages.length]);

  const getRoleTag = (role) => {
    const tags = {
      client: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
      talent: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200',
      dao: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200'
    };
    return tags[role] || 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
  };

  // Extract media from all messages (images, files, links)
  const allMedia = useMemo(() => {
    const media = {
      images: [],
      files: [],
      links: []
    };

    [...chatMessages, ...collaborationMessages].forEach(msg => {
      // Extract attachments
      if (msg.attachments && Array.isArray(msg.attachments)) {
        msg.attachments.forEach(att => {
          if (att.mimeType?.startsWith('image/') || att.type === 'image') {
            media.images.push({ ...att, messageId: msg._id, sender: msg.senderId?.username || 'Unknown' });
          } else if (att.url) {
            media.files.push({ ...att, messageId: msg._id, sender: msg.senderId?.username || 'Unknown' });
          }
        });
      }
      
      // Extract links from message content
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const content = msg.content || msg.message || '';
      const links = content.match(urlRegex);
      if (links) {
        links.forEach(link => {
          media.links.push({ url: link, messageId: msg._id, sender: msg.senderId?.username || 'Unknown' });
        });
      }
    });

    return media;
  }, [chatMessages, collaborationMessages]);

  // Get job/gig details for preview
  const workItem = proposal.disputeContext?.job;
  const jobModel = proposal.disputeContext?.jobModel;
  const workTitle = jobModel === 'Job'
    ? (workItem?.title || workItem?.jobTitle || 'Job')
    : jobModel === 'Order'
    ? (workItem?.gig?.title || 'Gig order')
    : jobModel === 'Chat'
    ? (workItem?.gig?.title || workItem?.job?.title || 'Conversation')
    : '';
  // Get amount - can be budget (Job), amount (Order), or amount (Chat)
  const workAmount = jobModel === 'Job' ? workItem?.budget : 
                     jobModel === 'Order' ? workItem?.amount : 
                     jobModel === 'Chat' ? (workItem?.amount || workItem?.job?.budget) : null;
  const workId = workItem?._id || workItem;
  const workLink = jobModel === 'Job' ? `/jobs/${workId}` :
                   jobModel === 'Order' ? `/gigs/${workItem?.gig?._id || workItem?.gig}` :
                   jobModel === 'Chat' ? (workItem?.gig?._id ? `/gigs/${workItem.gig._id}` : workItem?.job?._id ? `/jobs/${workItem.job._id}` : null) : null;
  const showWorkItemCard = Boolean(workItem && jobModel && (workTitle || jobModel === 'Job'));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-heading mb-2 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-accent" />
          Dispute Collaboration Room
        </h3>
        <p className="text-sm text-gray-500">
          All participants (Client, Talent, and DAO members) collaborate here. Previous conversation history is shown below, followed by ongoing DAO discussion.
        </p>
      </div>

      {/* Job/Gig Details Preview */}
      {showWorkItemCard && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gradient-to-r from-accent/5 to-accent/10 dark:from-accent/10 dark:to-accent/20">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="w-4 h-4 text-accent" />
                <h4 className="text-sm font-semibold text-heading">Work Item Details</h4>
              </div>
              <p className="text-sm font-medium text-heading mb-2">{workTitle}</p>
              <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <span className="px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    {jobModel}
                  </span>
                </span>
                {workAmount && (
                  <span className="flex items-center gap-1 font-semibold text-heading">
                    <DollarSign className="w-3 h-3" />
                    ${typeof workAmount === 'string' ? workAmount : workAmount.toLocaleString()}
                  </span>
                )}
                {workLink && (
                  <a 
                    href={workLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-accent hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View Details
                  </a>
                )}
              </div>
              
              {/* Settlement Section - Always visible for dispute proposals */}
              {proposal.proposalType === 'dispute' && (() => {
                // Read settlement directly from proposal to ensure it's always current
                const currentSettlement = proposal?.disputeContext?.settlement;
                return (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-sm font-semibold text-heading">Settlement Agreement</h5>
                      {canSetSettlement && (
                        <button
                          onClick={onOpenSettlementModal}
                          className="text-xs px-2 py-1 rounded bg-accent/10 text-accent hover:bg-accent/20 transition-colors flex items-center gap-1"
                          title="Set or update settlement amounts"
                        >
                          <Settings className="w-3 h-3" />
                          {currentSettlement?.talentAmount !== null && currentSettlement?.talentAmount !== undefined || 
                           currentSettlement?.clientAmount !== null && currentSettlement?.clientAmount !== undefined 
                            ? 'Update' : 'Set'} Amounts
                        </button>
                      )}
                    </div>
                    <div className="space-y-3">
                      {/* Talent Refund */}
                      <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                        <div className="flex-1">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Talent payout (USDC)</span>
                          <span className="text-base font-semibold text-heading">
                            {currentSettlement?.talentAmount !== null && currentSettlement?.talentAmount !== undefined
                              ? `${Number(currentSettlement.talentAmount).toLocaleString()} USDC`
                              : <span className="text-gray-400 italic">Not set</span>}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {currentSettlement?.talentApproved ? (
                            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-200">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs font-medium">Approved</span>
                            </div>
                          ) : (
                            <button
                              onClick={onApproveSettlement}
                              disabled={approveLoading || !isTalent || !currentSettlement?.talentAmount}
                              className={`text-xs px-3 py-1.5 rounded font-medium transition-colors flex items-center gap-1 ${
                                isTalent && currentSettlement?.talentAmount
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-900/60'
                                  : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-600 cursor-not-allowed'
                              }`}
                              title={!isTalent ? 'Only the talent can approve' : !currentSettlement?.talentAmount ? 'Amount must be set first' : 'Approve talent refund'}
                            >
                              {approveLoading ? "Approving..." : "Talent Approve"}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Client Refund */}
                      <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                        <div className="flex-1">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Client payout (USDC)</span>
                          <span className="text-base font-semibold text-heading">
                            {currentSettlement?.clientAmount !== null && currentSettlement?.clientAmount !== undefined
                              ? `${Number(currentSettlement.clientAmount).toLocaleString()} USDC`
                              : <span className="text-gray-400 italic">Not set</span>}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {currentSettlement?.clientApproved ? (
                            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-200">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs font-medium">Approved</span>
                            </div>
                          ) : (
                            <button
                              onClick={onApproveSettlement}
                              disabled={approveLoading || !isClient || !currentSettlement?.clientAmount}
                              className={`text-xs px-3 py-1.5 rounded font-medium transition-colors flex items-center gap-1 ${
                                isClient && currentSettlement?.clientAmount
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-900/60'
                                  : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-600 cursor-not-allowed'
                              }`}
                              title={!isClient ? 'Only the client can approve' : !currentSettlement?.clientAmount ? 'Amount must be set first' : 'Approve client refund'}
                            >
                              {approveLoading ? "Approving..." : "Client Approve"}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Status Messages */}
                      {currentSettlement?.clientApproved && currentSettlement?.talentApproved && (
                        <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                          <p className="text-sm text-green-700 dark:text-green-300 font-medium flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />
                            Both parties approved. Voting countdown stopped.
                          </p>
                        </div>
                      )}

                      {/* Resolve Button */}
                      {currentSettlement?.clientApproved && currentSettlement?.talentApproved && canResolveSettlement && (
                        <div className="w-full mt-2">
                          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-2 mb-2">
                            <p className="text-xs text-amber-800 dark:text-amber-200 flex items-center gap-2">
                              <Wallet className="w-3 h-3" />
                              Payouts are USDC from the DAO pool. Connect your DAO member wallet and confirm resolveDisputeWithAuth (small amount of ETH on Base required for gas).
                            </p>
                          </div>
                          <button
                            onClick={onResolveSettlement}
                            disabled={resolveLoading}
                            className="w-full px-4 py-2.5 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors text-sm font-semibold flex items-center justify-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            {resolveLoading ? "Resolving..." : "Resolve Dispute"}
                          </button>
                        </div>
                      )}

                      {!currentSettlement && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic text-center py-2">
                          No settlement amounts set yet. DAO members can set amounts using the button above.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Contract Status History & Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Contract Status Timeline */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="w-4 h-4 text-accent" />
            <h4 className="text-sm font-semibold text-heading">Contract Status History</h4>
          </div>
          {isLoadingHistory ? (
            <div className="text-center py-4 text-sm text-gray-500">Loading...</div>
          ) : contractHistory?.statusHistory && contractHistory.statusHistory.length > 0 ? (
            <div className="space-y-3">
              {contractHistory.statusHistory.map((status, index) => (
                <div
                  key={index}
                  className={`relative pl-6 pb-4 ${
                    index < contractHistory.statusHistory.length - 1
                      ? 'border-l-2 border-gray-200 dark:border-gray-700'
                      : ''
                  }`}
                >
                  <div className="absolute left-0 top-0 w-3 h-3 rounded-full bg-accent border-2 border-white dark:border-gray-900 -translate-x-[7px]" />
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          status.status === contractHistory.currentStatus
                            ? 'bg-accent text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}>
                          {status.label}
                          {status.status === contractHistory.currentStatus && ' (Current)'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          status.role === 'client'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200'
                        }`}>
                          {status.role === 'client' ? 'Client' : 'Talent'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {status.performedByUser?.username || 'Unknown'}
                      </p>
                      {status.occurredAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          {format(new Date(status.occurredAt), "PPpp")}
                        </p>
                      )}
                      {status.txHash && (
                        <a
                          href={`https://etherscan.io/tx/${status.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-accent hover:underline mt-1 inline-flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View Transaction
                        </a>
                      )}
                      {status.amountUSD && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          Amount: ${status.amountUSD.toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-gray-500">
              No contract status history available
            </div>
          )}
        </div>

        {/* Transactions List */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-4 h-4 text-accent" />
            <h4 className="text-sm font-semibold text-heading">Transactions</h4>
          </div>
          {isLoadingHistory ? (
            <div className="text-center py-4 text-sm text-gray-500">Loading...</div>
          ) : contractHistory?.transactions && contractHistory.transactions.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {contractHistory.transactions.map((tx) => (
                <div
                  key={tx._id}
                  className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          tx.type === 'escrow_deposit'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200'
                        }`}>
                          {tx.type === 'escrow_deposit' ? 'Deposit' : 'Disbursement'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {format(new Date(tx.createdAt), "PPpp")}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-heading">
                        ${tx.amount.toLocaleString()} {tx.currency || 'USD'}
                      </p>
                      {tx.fromUser && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          From: {tx.fromUser.username || 'Unknown'}
                        </p>
                      )}
                      {tx.toUser && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          To: {tx.toUser.username || 'Unknown'}
                        </p>
                      )}
                      {tx.txHash && (
                        <a
                          href={`https://etherscan.io/tx/${tx.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-accent hover:underline mt-1 inline-flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Transaction ID: {tx.txHash.slice(0, 10)}...{tx.txHash.slice(-8)}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-gray-500">
              No transactions recorded yet
            </div>
          )}
        </div>
      </div>

      {/* Media Gallery */}
      {(allMedia.images.length > 0 || allMedia.files.length > 0 || allMedia.links.length > 0) && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="w-4 h-4 text-accent" />
            <h4 className="text-sm font-semibold text-heading">Media & Attachments</h4>
          </div>
          
          {/* Images */}
          {allMedia.images.length > 0 && (
            <div className="mb-4">
              <h5 className="text-xs uppercase text-gray-500 mb-2">Images ({allMedia.images.length})</h5>
              <div className="grid grid-cols-4 gap-2">
                {allMedia.images.map((img, idx) => (
                  <a
                    key={idx}
                    href={img.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group"
                  >
                    <img 
                      src={img.url} 
                      alt={img.originalName || img.filename || 'Image'}
                      className="w-full h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700 hover:border-accent transition-colors"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors flex items-center justify-center">
                      <ImageIcon className="w-4 h-4 text-white opacity-0 group-hover:opacity-100" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Files */}
          {allMedia.files.length > 0 && (
            <div className="mb-4">
              <h5 className="text-xs uppercase text-gray-500 mb-2">Files ({allMedia.files.length})</h5>
              <div className="space-y-1">
                {allMedia.files.map((file, idx) => (
                  <a
                    key={idx}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-accent hover:bg-accent/5 transition-colors"
                  >
                    <File className="w-4 h-4 text-gray-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-300 flex-1 truncate">
                      {file.originalName || file.filename || 'File'}
                    </span>
                    <ExternalLink className="w-3 h-3 text-gray-400" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Links */}
          {allMedia.links.length > 0 && (
            <div>
              <h5 className="text-xs uppercase text-gray-500 mb-2">Links ({allMedia.links.length})</h5>
              <div className="space-y-1">
                {allMedia.links.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-accent hover:bg-accent/5 transition-colors"
                  >
                    <LinkIcon className="w-4 h-4 text-gray-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-300 flex-1 truncate">
                      {link.url}
                    </span>
                    <ExternalLink className="w-3 h-3 text-gray-400" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Unified Message Thread */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-heading">
                Conversation Thread
              </span>
              <span className="text-xs text-gray-500">
                {allMessages.length} {allMessages.length === 1 ? 'message' : 'messages'}
              </span>
            </div>
            {chatMessages.length > 0 && (
              <span className="text-xs text-gray-500">
                {chatMessages.length} from previous chat
              </span>
            )}
          </div>
        </div>
        
        <div className="p-4 max-h-[500px] overflow-y-auto space-y-4">
          {allMessages.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm">No messages yet. Start the conversation below.</p>
            </div>
          ) : (
            <>
              {chatMessages.length > 0 && (
                <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Previous Conversation
                    </span>
                    <span className="text-xs text-gray-400">
                      ({chatMessages.length} messages)
                    </span>
                  </div>
                </div>
              )}
              
              {allMessages.map((message, index) => {
                const msgSenderId = message.senderId?.toString();
                const isOwn = user && (
                  user._id?.toString() === msgSenderId || 
                  user.id?.toString() === msgSenderId
                );
                const showDivider = chatMessages.length > 0 && 
                                   index === chatMessages.length - 1 && 
                                   collaborationMessages.length > 0;
                
                return (
                  <Fragment key={message._id || `msg-${index}`}>
                    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className={`flex flex-col ${isOwn ? 'items-end max-w-[70%]' : 'items-start max-w-[70%]'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${getRoleTag(message.role)}`}>
                            {message.role}
                          </span>
                          <span className="text-xs font-medium text-heading">
                            {message.sender}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                        <div className={`rounded-lg px-4 py-2.5 text-sm ${
                          isOwn 
                            ? message.role === 'dao' 
                              ? 'bg-purple-500 text-white' 
                              : 'bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                        }`}>
                          {message.content}
                          {/* Display attachments if any */}
                          {(message.attachments || message.attachment) && (
                            <div className="mt-2 space-y-2">
                              {(message.attachments || [message.attachment]).filter(Boolean).map((att, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  {att.mimeType?.startsWith('image/') || att.type === 'image' ? (
                                    <a href={att.url} target="_blank" rel="noopener noreferrer" className="block">
                                      <img src={att.url} alt={att.originalName || att.filename} className="max-w-xs rounded-lg" />
                                    </a>
                                  ) : (
                                    <a 
                                      href={att.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 text-xs underline"
                                    >
                                      <File className="w-4 h-4" />
                                      {att.originalName || att.filename || 'Attachment'}
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {showDivider && (
                      <div className="my-4 py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            DAO Collaboration Thread
                          </span>
                          <span className="text-xs text-gray-400">
                            ({collaborationMessages.length} messages)
                          </span>
                        </div>
                      </div>
                    )}
                  </Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* DAO Message Input */}
        {canSendMessage && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40">
            <label className="text-xs uppercase text-gray-500 mb-2 block">
              Send DAO Message
            </label>
            
            {/* Attachment Preview */}
            {messageAttachments.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {messageAttachments.map((attachment) => (
                  <div key={attachment.id} className="relative group">
                    {attachment.preview ? (
                      <div className="relative">
                        <img 
                          src={attachment.preview} 
                          alt={attachment.name}
                          className="w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                        />
                        <button
                          onClick={() => onRemoveAttachment(attachment.id)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <File className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-600 dark:text-gray-300 max-w-[100px] truncate">
                          {attachment.name}
                        </span>
                        <button
                          onClick={() => onRemoveAttachment(attachment.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex gap-2">
              <div className="flex-1 flex flex-col gap-2">
                <textarea
                  className="input-field h-20 resize-none"
                  placeholder="Request clarifications, suggest actions, or ask for supporting evidence..."
                  value={messageText}
                  onChange={(event) => onMessageChange(event.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && (messageText.trim() || messageAttachments.length > 0)) {
                      e.preventDefault();
                      onSendMessage();
                    }
                  }}
                  disabled={isSending}
                />
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={onFileSelect}
                    multiple
                    className="hidden"
                    accept="image/*,application/pdf,.doc,.docx,.txt"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-accent border border-gray-200 dark:border-gray-700 rounded-lg hover:border-accent transition-colors"
                    disabled={isSending}
                  >
                    <Paperclip className="w-4 h-4" />
                    Attach
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      fileInputRef.current?.click();
                      if (fileInputRef.current) {
                        fileInputRef.current.accept = 'image/*';
                      }
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-accent border border-gray-200 dark:border-gray-700 rounded-lg hover:border-accent transition-colors"
                    disabled={isSending}
                  >
                    <ImageIcon className="w-4 h-4" />
                    Image
                  </button>
                </div>
              </div>
              <button
                className="btn-primary self-end"
                disabled={(!messageText.trim() && messageAttachments.length === 0) || isSending}
                onClick={onSendMessage}
              >
                {isSending ? "Sending..." : "Send"}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Only eligible DAO members (9+ activity points) can send messages
            </p>
          </div>
        )}
      </div>

      {/* General Comments Section */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
        <h4 className="text-sm font-semibold text-heading mb-3 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-accent" />
          DAO General Comments
        </h4>
        <p className="text-xs text-gray-500 mb-4">
          Use comments to summarize recommended outcomes or guiding principles for this dispute.
        </p>
        
        <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
          {!proposal.disputeContext?.generalComments || proposal.disputeContext.generalComments.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No general comments yet. Be the first to share your perspective.
            </div>
          ) : (
            proposal.disputeContext.generalComments.map((entry) => (
              <div
                key={entry._id}
                className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/40"
              >
                <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                  <span className="font-medium text-heading">
                    {entry.commenter?.username || "DAO member"}
                  </span>
                  <span>
                    {formatDistanceToNow(new Date(entry.createdAt), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
                  {entry.comment}
                </p>
              </div>
            ))
          )}
        </div>
        
        {canComment && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <label className="text-xs uppercase text-gray-500 mb-2 block">
              Add General Comment
            </label>
            <textarea
              className="input-field h-24 mb-2"
              placeholder="Summarize recommended outcomes or guiding principles for this dispute..."
              value={commentText}
              onChange={(event) => onCommentChange(event.target.value)}
              disabled={isCommentSubmitting}
            />
            <div className="flex justify-end">
              <button
                className="btn-primary"
                disabled={!commentText.trim() || isCommentSubmitting}
                onClick={onAddComment}
              >
                {isCommentSubmitting ? "Posting..." : "Share Comment"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PlatformDetails = ({ proposal }) => {
  const platformDetails = proposal.platformDetails || {};
  const hasData = Object.keys(platformDetails).length > 0 && 
    Object.values(platformDetails).some(val => val && val.trim() !== '');

  if (!hasData) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-heading">Platform analysis</h3>
          <p className="text-sm text-gray-500">
            Focus on strategic implications, timeline fit, and dependencies.
          </p>
        </div>
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No platform analysis data available for this proposal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-heading">Platform analysis</h3>
        <p className="text-sm text-gray-500">
          Focus on strategic implications, timeline fit, and dependencies.
        </p>
      </div>
      {platformDetails.problemStatement && (
        <DetailBlock
          title="Problem statement"
          content={platformDetails.problemStatement}
        />
      )}
      {platformDetails.proposedSolution && (
        <DetailBlock
          title="Proposed solution"
          content={platformDetails.proposedSolution}
        />
      )}
      {platformDetails.impact && (
        <DetailBlock title="Impact" content={platformDetails.impact} />
      )}
      {platformDetails.implementationPlan && (
        <DetailBlock
          title="Implementation plan"
          content={platformDetails.implementationPlan}
        />
      )}
      {platformDetails.successMetrics && (
        <DetailBlock
          title="Success metrics"
          content={platformDetails.successMetrics}
        />
      )}
      {platformDetails.dependencies && (
        <DetailBlock
          title="Dependencies"
          content={platformDetails.dependencies}
        />
      )}
    </div>
  );
};

const DisputeDetails = ({ proposal }) => {
  const ctx = proposal.disputeContext || {};

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-heading">Dispute overview</h3>
        <p className="text-sm text-gray-500">
          DAO voters review both narratives and interact with parties directly in
          the collaboration feed.
        </p>
      </div>
      <DetailBlock
        title="Issue summary"
        content={ctx.issueSummary}
      />
      <DetailBlock
        title="Client narrative"
        content={ctx.clientNarrative}
      />
      <DetailBlock
        title="Talent narrative"
        content={ctx.talentNarrative}
      />
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <h4 className="text-sm font-semibold text-heading mb-2">
          Work context
        </h4>
        <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-400" />
            Client: {ctx.client?.username || "N/A"}
          </div>
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-400" />
            Talent: {ctx.talent?.username || "N/A"}
          </div>
          {ctx.job && (
            <div className="space-y-1 pt-2">
              <p className="text-xs uppercase text-gray-500">Work item</p>
              {ctx.job.type === "Job" ? (
                <>
                  <p className="font-medium text-heading">{ctx.job.title}</p>
                  <p className="text-xs text-gray-500">Status: {ctx.job.status}</p>
                </>
              ) : (
                <>
                  <p className="font-medium text-heading">
                    Gig order {ctx.job.orderNumber || ctx.job._id}
                  </p>
                  <p className="text-xs text-gray-500">Status: {ctx.job.status}</p>
                  {ctx.job.gig?.title && (
                    <p className="text-xs text-gray-500">
                      Gig: {ctx.job.gig.title}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DetailBlock = ({ title, content }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900/60">
    <h4 className="text-xs uppercase text-gray-500 mb-2">{title}</h4>
    <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
      {content || "—"}
    </p>
  </div>
);

const StatusPill = ({ status }) => {
  const label = status.replace("_", " ");
  const palette = {
    voting: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200",
    awaiting_resolution:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200",
    passed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200",
    rejected: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200",
    resolved:
      "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  };
  const className =
    palette[status] ||
    "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200";

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${className}`}>
      {label}
    </span>
  );
};

export default GovernanceProposalDetailPage;

